/**
 * ReminderSettings Component
 *
 * Allows users to configure email reminders for meal preparation.
 * Users can create reminders for specific meal slots with configurable
 * time and frequency (once, daily, weekly).
 */

import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useReminderStore } from '../../stores/reminderStore';
import { useMealPlanStore } from '../../stores/mealPlanStore';
import type { ReminderFrequency } from '../../types/reminder';
import type { MealSlot } from '../../types/mealPlan';
import '../auth/auth.css';
import '../recipes/recipes.css';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function ReminderSettings() {
  const { t } = useTranslation(['settings', 'common', 'mealPlan']);

  const FREQUENCY_OPTIONS: { value: ReminderFrequency; label: string }[] = [
    { value: 'once', label: t('once') },
    { value: 'daily', label: t('daily') },
    { value: 'weekly', label: t('weekly') },
  ];

  const {
    reminders,
    isLoading: isLoadingReminders,
    error: reminderError,
    fetchReminders,
    createReminder,
    updateReminder,
    deleteReminder,
    clearError,
  } = useReminderStore();

  const {
    currentMealPlan,
    isLoading: isLoadingMealPlan,
    getCurrentWeekPlan,
  } = useMealPlanStore();

  // Form state
  const [selectedMealSlotId, setSelectedMealSlotId] = useState<number | ''>('');
  const [reminderTime, setReminderTime] = useState('18:00'); // Default to 6 PM
  const [frequency, setFrequency] = useState<ReminderFrequency>('weekly');
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Load current week's meal plan and reminders
  useEffect(() => {
    getCurrentWeekPlan().catch(() => {
      // Error is handled in the store
    });
    fetchReminders().catch(() => {
      // Error is handled in the store
    });
  }, [getCurrentWeekPlan, fetchReminders]);

  // Get meal slots with recipes
  const mealSlotsWithRecipes = (currentMealPlan?.meal_slots || []).filter(
    (slot) => slot.recipe_id !== null
  );

  const getMealSlotLabel = (slot: MealSlot): string => {
    const day = t(`mealPlan:${DAY_KEYS[slot.day]}`);
    const mealType = t(`mealPlan:${slot.meal_type}`);
    const recipeName = slot.recipe?.title || t('unknownMeal');
    return `${day} ${mealType} - ${recipeName}`;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    setValidationError(null);

    // Validation
    if (!selectedMealSlotId) {
      setValidationError(t('selectMealSlotError'));
      return;
    }

    if (!reminderTime) {
      setValidationError(t('selectTimeError'));
      return;
    }

    const formData = {
      meal_slot_id: Number(selectedMealSlotId),
      reminder_time: reminderTime,
      frequency,
    };

    try {
      if (editingReminderId) {
        await updateReminder(editingReminderId, formData);
      } else {
        await createReminder(formData);
      }

      // Reset form
      setSelectedMealSlotId('');
      setReminderTime('18:00');
      setFrequency('weekly');
      setEditingReminderId(null);
    } catch (error) {
      // Error is handled in the store
    }
  };

  const handleEdit = (reminderId: number) => {
    const reminder = reminders.find((r) => r.id === reminderId);
    if (!reminder) return;

    setEditingReminderId(reminderId);
    setSelectedMealSlotId(reminder.meal_slot_id);
    setReminderTime(reminder.reminder_time);
    setFrequency(reminder.frequency);
  };

  const handleDelete = async (reminderId: number) => {
    if (!window.confirm(t('deleteConfirm'))) {
      return;
    }

    try {
      await deleteReminder(reminderId);
    } catch (error) {
      // Error is handled in the store
    }
  };

  const handleCancelEdit = () => {
    setEditingReminderId(null);
    setSelectedMealSlotId('');
    setReminderTime('18:00');
    setFrequency('weekly');
    setValidationError(null);
    clearError();
  };

  const displayError = validationError || reminderError;
  const isLoading = isLoadingReminders || isLoadingMealPlan;

  return (
    <div className="recipe-container">
      <div className="recipe-header">
        <h1>{t('reminderSettings')}</h1>
        <p className="recipe-subtitle">
          {t('reminderSubtitle')}
        </p>
      </div>

      {displayError && (
        <div className="error-message" role="alert">
          {displayError}
        </div>
      )}

      {/* Create/Edit Reminder Form */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>
          {editingReminderId ? t('editReminder') : t('createNewReminder')}
        </h2>

        {mealSlotsWithRecipes.length === 0 ? (
          <div style={{ padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
            <p>{t('noMealsPlanned')}</p>
            <a href="/meal-plan" className="btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
              {t('common:goToMealPlan')}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form" style={{ maxWidth: '600px' }}>
            <div className="form-group">
              <label htmlFor="meal-slot">
                {t('mealSlot')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="meal-slot"
                value={selectedMealSlotId}
                onChange={(e) => setSelectedMealSlotId(e.target.value ? Number(e.target.value) : '')}
                required
                disabled={isLoading}
              >
                <option value="">{t('selectMealSlot')}</option>
                {mealSlotsWithRecipes.map((slot) => (
                  <option key={slot.id} value={slot.id}>
                    {getMealSlotLabel(slot)}
                  </option>
                ))}
              </select>
              <small className="form-hint">{t('mealSlotHint')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="reminder-time">
                {t('reminderTime')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                id="reminder-time"
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                required
                disabled={isLoading}
              />
              <small className="form-hint">
                {t('reminderTimeHint')}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="frequency">
                {t('frequency')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as ReminderFrequency)}
                required
                disabled={isLoading}
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <small className="form-hint">{t('frequencyHint')}</small>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn-primary" disabled={isLoading} style={{ flex: 1 }}>
                {isLoading
                  ? editingReminderId
                    ? t('updating')
                    : t('creating')
                  : editingReminderId
                  ? t('updateReminder')
                  : t('createReminder')}
              </button>
              {editingReminderId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                >
                  {t('common:cancel')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Existing Reminders List */}
      <div>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>{t('yourReminders')}</h2>

        {reminders.length === 0 ? (
          <div style={{ padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
            <p>{t('noReminders')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {reminders.map((reminder) => {
              const mealSlot = mealSlotsWithRecipes.find((slot) => slot.id === reminder.meal_slot_id);
              return (
                <div
                  key={reminder.id}
                  style={{
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                  }}
                >
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      {mealSlot ? getMealSlotLabel(mealSlot) : t('unknownMeal')}
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      <strong>{t('time')}</strong> {reminder.reminder_time}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      <strong>{t('frequencyLabel')}</strong> {reminder.frequency}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEdit(reminder.id)}
                      disabled={isLoading}
                      className="btn-secondary"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                    >
                      {t('common:edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(reminder.id)}
                      disabled={isLoading}
                      className="btn-secondary"
                      style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        backgroundColor: '#fee',
                        color: '#dc2626',
                      }}
                    >
                      {t('common:delete')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReminderSettings;
